import { useEffect, useState, useCallback } from 'react'
import { Plus, CheckCircle2, Trash2, DollarSign } from 'lucide-react'
import api from '../api/client'
import { useAuth } from '../auth'
import Pagination from '../components/Pagination'
import SearchableSelect from '../components/SearchableSelect'
import { confirmModal, successModal, errorModal } from '../components/ui'

const STATUS = {
  pendente: 'bg-amber-100 text-amber-800 border-amber-200',
  paga: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelada: 'bg-red-100 text-red-700 border-red-200',
  estornada: 'bg-rose-100 text-rose-700 border-rose-200',
}
const money = (v) => `R$ ${Number(v || 0).toFixed(2)}`
const mesAtual = () => new Date().toISOString().slice(0, 7)

export default function Faturas() {
  const { user } = useAuth()
  const isAdmin = user?.perfil === 'admin'
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(20)
  const [meta, setMeta] = useState({ total: 0, pages: 1 })
  const [status, setStatus] = useState('')
  const [resumo, setResumo] = useState({ total_pendente: 0, total_pago: 0 })
  const [lics, setLics] = useState([])
  const [clientesList, setClientes] = useState([])
  const [ofertasList, setOfertas] = useState([])
  const [nova, setNova] = useState(null)

  const load = useCallback((p = 1, sz = size) => {
    const q = status ? `&status=${status}` : ''
    api.get(`/faturas?page=${p}&limit=${sz}${q}`).then((r) => {
      setRows(r.data.data || []); setMeta({ total: r.data.total || 0, pages: r.data.pages || 1 }); setPage(p)
    }).catch(() => {})
    api.get('/faturas/_resumo').then((r) => setResumo(r.data)).catch(() => {})
  }, [status, size])

  useEffect(() => { load(1) }, [load])
  useEffect(() => {
    if (!isAdmin) return
    api.get('/licenciados?limit=100').then((r) => setLics(r.data.data || [])).catch(() => {})
    api.get('/clientes?limit=100').then((r) => setClientes(r.data.data || [])).catch(() => {})
    api.get('/ofertas?limit=200').then((r) => setOfertas(r.data.data || [])).catch(() => {})
  }, [isAdmin])

  async function pagar(f) {
    if (!(await confirmModal('Confirmar pagamento desta fatura? A comissão de recorrência será gerada.', { title: 'Marcar como paga', okLabel: 'Confirmar' }))) return
    try { const r = await api.post(`/faturas/${f.id}/pagar`); await successModal(`Fatura paga. Comissão RO gerada: ${money(r.data.comissao)}`, { title: 'Pago!' }); load(page) }
    catch (e) { errorModal(e?.response?.data?.detail || 'Erro') }
  }
  async function excluir(f) {
    if (!(await confirmModal('Excluir esta fatura?', { title: 'Excluir', danger: true, okLabel: 'Excluir' }))) return
    try { await api.delete(`/faturas/${f.id}`); load(page) } catch { /* noop */ }
  }
  async function salvarNova() {
    try {
      const body = { ...nova, valor: Number(nova.valor || 0) }
      if (nova.contrato_adesao_id) body.contrato_adesao_id = Number(nova.contrato_adesao_id)
      if (nova.cliente_id) body.cliente_id = Number(nova.cliente_id)
      if (nova.oferta_id) body.oferta_id = Number(nova.oferta_id)
      if (nova.consumo_kwh) body.consumo_kwh = Number(nova.consumo_kwh)
      await api.post('/faturas', body); setNova(null); load(1); successModal('Fatura lançada.')
    } catch (e) { errorModal(e?.response?.data?.detail || 'Erro ao lançar fatura') }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-gray-900">Faturas · Recorrência</h1>
          <p className="text-sm text-gray-500 mt-0.5">{isAdmin ? 'Lançamento manual ou via API. Ao pagar, gera a comissão de recorrência.' : 'Suas faturas recorrentes e status.'}</p>
        </div>
        {isAdmin && (
          <button onClick={() => setNova({ competencia: mesAtual(), origem: 'manual', valor: '' })}
            className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-semibold shadow-md hover:brightness-95">
            <Plus size={16} /> Nova fatura
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs uppercase tracking-wide text-gray-500">Pendente</p>
          <p className="font-heading text-2xl font-bold text-amber-600">{money(resumo.total_pendente)}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs uppercase tracking-wide text-gray-500">Pago</p>
          <p className="font-heading text-2xl font-bold text-emerald-600">{money(resumo.total_pago)}</p>
        </div>
        <div className="hidden sm:block bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <p className="text-xs uppercase tracking-wide text-gray-500">Filtrar status</p>
          <SearchableSelect value={status} onChange={(v) => setStatus(v)} emptyLabel="Todos" className="mt-1"
            options={[{ value: 'pendente', label: 'Pendente' }, { value: 'paga', label: 'Paga' }, { value: 'cancelada', label: 'Cancelada' }]} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">#</th><th className="px-4 py-3">Licenciado</th><th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Competência</th><th className="px-4 py-3">Valor</th>
              <th className="px-4 py-3">Origem</th><th className="px-4 py-3">Status</th>
              {isAdmin && <th className="px-4 py-3 text-right">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} className="border-t border-gray-100 hover:bg-primary-50/30">
                <td className="px-4 py-3 text-gray-400">{f.id}</td>
                <td className="px-4 py-3">{f.licenciado_nome || '—'}</td>
                <td className="px-4 py-3">{f.cliente_nome || '—'}</td>
                <td className="px-4 py-3">{f.competencia}</td>
                <td className="px-4 py-3 font-medium">{money(f.valor)}</td>
                <td className="px-4 py-3"><span className="text-xs text-gray-500">{f.origem}</span></td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${STATUS[f.status] || ''}`}>{f.status}</span></td>
                {isAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {f.status === 'pendente' && (
                        <button onClick={() => pagar(f)} title="Marcar paga"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"><CheckCircle2 size={16} /></button>
                      )}
                      <button onClick={() => excluir(f)} title="Excluir"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={16} /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-8 text-center text-gray-400">Nenhuma fatura.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pages={meta.pages} total={meta.total} pageSize={size} onPage={load} onPageSize={(n) => { setSize(n); load(1, n) }} />

      {nova && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3">
            <h2 className="text-lg font-heading font-bold text-gray-900">Nova fatura</h2>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Cliente (herda licenciado/parceiro/serviço)</label>
              <SearchableSelect value={nova.cliente_id || ''} onChange={(v) => setNova({ ...nova, cliente_id: v })}
                placeholder="Buscar cliente..." options={clientesList.map((c) => ({ value: c.id, label: `${c.nome}${c.cpf_cnpj ? ' · ' + c.cpf_cnpj : ''}` }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Licenciado (se não escolher cliente)</label>
              <SearchableSelect value={nova.licenciado_id || ''} onChange={(v) => setNova({ ...nova, licenciado_id: v })}
                placeholder="Buscar licenciado..." options={lics.map((l) => ({ value: l.id, label: l.nome }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Produto/Serviço (define a regra de PV)</label>
              <SearchableSelect value={nova.oferta_id || ''} onChange={(v) => setNova({ ...nova, oferta_id: v })}
                placeholder="Buscar oferta..." options={ofertasList.map((o) => ({ value: o.id, label: o.nome }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Competência</label>
                <input value={nova.competencia} onChange={(e) => setNova({ ...nova, competencia: e.target.value })} placeholder="2026-06"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Valor (R$)</label>
                <input type="number" value={nova.valor} onChange={(e) => setNova({ ...nova, valor: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Contrato/Conexão (opcional, p/ cashback)</label>
              <input type="number" value={nova.contrato_adesao_id || ''} onChange={(e) => setNova({ ...nova, contrato_adesao_id: e.target.value })} placeholder="ID da conexão"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setNova(null)} className="px-4 py-2 text-gray-500">Cancelar</button>
              <button onClick={salvarNova} className="rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-5 py-2 font-semibold shadow-md">Lançar fatura</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
